import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ConeTecMark } from './icons'
import {
  ASK_ONE_FILTERS,
  CORPUS_WORDS,
  OUTCOME_WORDS,
  REVIEW_REASONS,
  THEME_ACTIONS,
  dayLabel,
  pct,
  reasonLabel,
  spanLabel,
  totalsOf,
  type AskOneDay,
  type AskOneFilter,
  type AskOneQuestion,
  type AskOneReason,
  type AskOneSummary,
  type AskOneTheme,
} from '../lib/askOneDesk'
import { ONE_REASONS } from '../lib/wiki'

/**
 * `#/support/ask-one` — the pieces (126, HLP-35). Pure components: the route
 * loads and acts, these render, so the web suite can reach every one of them.
 *
 * ── NOBODY IS NAMED, AND NOTHING HERE COULD NAME THEM ───────────────────────
 *
 * Every prop comes from two logs that carry no person (037, 048). The footer
 * line on the list says so, because the first thing a reader of this screen
 * will wonder is whether their colleagues' questions are attributed.
 *
 * ── TEAL MEANS A MACHINE WROTE IT ───────────────────────────────────────────
 *
 * The themes are the only machine-written thing on the screen, and only they
 * wear the teal and the mark. The questions are people's words; the counts are
 * the database's. Painting the whole tab teal would say a machine wrote the
 * questions.
 *
 * ── STAFF SCREEN, ENGLISH ON PURPOSE ────────────────────────────────────────
 *
 * Like curator mode, the admin screens and the lane holder's inbox.
 */

/* ── the tabs above both halves of #/support ─────────────────────────────── */

/**
 * `.tabs`, because these are SECTIONS of one screen (rule 18) — the shape
 * #/stars and #/admin/shop already use. Rendered only for analytics.read: a
 * lane holder has one section, and a tab bar with one tab is decoration.
 */
export function SupportTabs({
  on,
  deskLabel = 'Desk',
}: {
  on: 'desk' | 'ask-one' | 'one-question'
  deskLabel?: string
}) {
  const tab = (to: string, key: typeof on, label: string) => (
    <Link to={to} className={`tab${on === key ? ' tab--on' : ''}`} aria-current={on === key ? 'page' : undefined}>
      {label}
    </Link>
  )
  return (
    <nav className="tabs" aria-label="Support sections">
      {tab('/support', 'desk', deskLabel)}
      {tab('/support/ask-one', 'ask-one', 'Ask One')}
      {/* 131: where One Question's answers go — analytics.read, like Ask One. */}
      {tab('/support/one-question', 'one-question', 'One Question')}
    </nav>
  )
}

/* ── the tiles ───────────────────────────────────────────────────────────── */

export function AskOneTiles({ summary }: { summary: AskOneSummary }) {
  const t = totalsOf(summary.daily)
  const rated = summary.ratings.up + summary.ratings.down
  const tiles: Array<{ k: string; v: string; sub: string; mark?: string }> = [
    { k: 'Conversations started', v: String(t.conversations), sub: `first turns, last ${summary.days} days` },
    { k: 'Answered', v: String(t.direct), sub: `${pct(t.direct, t.conversations)} of conversations`, mark: 'direct' },
    { k: 'One inferred', v: String(t.inferred), sub: `${pct(t.inferred, t.conversations)} · each is an entry to write`, mark: 'inferred' },
    { k: 'Nobody could answer', v: String(t.none), sub: `${pct(t.none, t.conversations)} of conversations`, mark: 'none' },
    { k: 'Ratings', v: `▲ ${summary.ratings.up}  ▼ ${summary.ratings.down}`, sub: `${rated} rated · ${pct(rated, t.conversations)} of conversations` },
  ]
  return (
    <div className="askone__tiles">
      {tiles.map((x) => (
        <div className="askone__tile" key={x.k}>
          <span className="askone__tilek">
            {x.mark && <i className={`askone__sw askone__sw--${x.mark}`} aria-hidden="true" />}
            {x.k}
          </span>
          <span className="askone__tilev">{x.v}</span>
          <span className="askone__tilesub">{x.sub}</span>
        </div>
      ))}
      {t.unavailable > 0 && (
        <p className="askone__outage" role="note">
          One was unavailable for {t.unavailable} of these. Those are outages, not gaps in the answers, and are left out of
          “Nobody could answer”.
        </p>
      )}
    </div>
  )
}

/* ── the chart ───────────────────────────────────────────────────────────── */

/**
 * Stacked bars, one per day, every day of the period (the series has no gaps
 * by construction — `generate_series` in the function). One scale for marks,
 * ticks and labels; the top tick is the smallest multiple of 5 at or above the
 * busiest day, so no label names a value the chart does not reach.
 */
export function AskOneChart({ daily }: { daily: readonly AskOneDay[] }) {
  const W = 760
  const H = 190
  const L = 30
  const R = 8
  const T = 14
  const B = 26
  const peak = Math.max(1, ...daily.map((d) => d.direct + d.inferred + d.none + d.unavailable))
  const top = Math.max(5, Math.ceil(peak / 5) * 5)
  const ticks = [0, top / 5, (2 * top) / 5, (3 * top) / 5, (4 * top) / 5, top].filter((v) => Number.isInteger(v))
  const cw = (W - L - R) / Math.max(1, daily.length)
  const bw = Math.min(cw * 0.62, 30)
  const y = (v: number) => T + (H - T - B) * (1 - v / top)
  const every = daily.length > 10 ? 3 : 1
  return (
    <div className="askone__chartwrap">
      <svg className="askone__chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Conversations started per day, stacked by how One answered">
        {ticks.map((v) => (
          <g key={v}>
            <line className="askone__grid" x1={L} x2={W - R} y1={y(v)} y2={y(v)} />
            <text className="askone__tick" x={L - 6} y={y(v) + 3} textAnchor="end">{v}</text>
          </g>
        ))}
        {daily.map((d, i) => {
          const cx = L + cw * i + cw / 2
          const x = cx - bw / 2
          let base = 0
          const parts: Array<[number, string, string]> = [
            [d.direct, 'direct', 'answered'],
            [d.inferred, 'inferred', 'One inferred'],
            [d.none, 'none', 'nobody could answer'],
            [d.unavailable, 'unavailable', 'One unavailable'],
          ]
          const total = d.direct + d.inferred + d.none + d.unavailable
          return (
            <g key={d.day}>
              {parts.map(([n, cls, words]) => {
                if (!n) return null
                const y1 = y(base + n)
                const y0 = y(base)
                base += n
                return (
                  <rect key={cls} className={`askone__bar askone__bar--${cls}`} x={x} y={y1} width={bw} height={y0 - y1}>
                    <title>{`${dayLabel(d.day)}: ${n} ${words}`}</title>
                  </rect>
                )
              })}
              {total > 0 && (
                <text className="askone__barn" x={cx} y={y(total) - 4} textAnchor="middle">{total}</text>
              )}
              {(i % every === 0 || i === daily.length - 1) && (
                <text className="askone__tick" x={cx} y={H - 8} textAnchor="middle">{dayLabel(d.day)}</text>
              )}
            </g>
          )
        })}
        <line className="askone__axis" x1={L} x2={W - R} y1={y(0)} y2={y(0)} />
      </svg>
      <p className="askone__legend">
        <span><i className="askone__sw askone__sw--direct" aria-hidden="true" />Answered</span>
        <span><i className="askone__sw askone__sw--inferred" aria-hidden="true" />One inferred</span>
        <span><i className="askone__sw askone__sw--none" aria-hidden="true" />Nobody could answer</span>
        <span><i className="askone__sw askone__sw--unavailable" aria-hidden="true" />One unavailable</span>
      </p>
      <p className="askone__note">
        Only the first turn of each conversation is logged (086), so a ten-turn conversation counts once. Before 29 Sep an
        outage was logged as “nobody could answer”.
      </p>
    </div>
  )
}

/* ── the themes: machine-written ─────────────────────────────────────────── */

export function AskOneThemes({
  themes,
  activeId,
  onPick,
  days,
}: {
  themes: AskOneTheme[] | null
  activeId: string | null
  onPick: (id: string | null) => void
  days: number
}) {
  const writtenAt = themes?.[0]?.writtenAt
  return (
    <section className="askone__themes" aria-label="What people asked about">
      <p className="askone__by">
        <span className="askone__mark" aria-hidden="true"><ConeTecMark size={14} /></span>
        What people asked about · written by One{writtenAt ? ` · ${new Date(writtenAt).toLocaleDateString('en-CA')}` : ''} · a suggestion, not a decision
      </p>
      {themes === null ? (
        <p className="askone__empty">Loading…</p>
      ) : themes.length === 0 ? (
        <p className="askone__empty">
          No themes for the last {days} days yet. The 07:00 run reads the questions and writes them here.
        </p>
      ) : (
        <ul className="askone__themelist">
          {themes.map((t) => {
            const on = activeId === t.id
            const w = (n: number) => `${t.asks > 0 ? (n * 100) / t.asks : 0}%`
            return (
              <li key={t.id}>
                <button type="button" className={`askone__theme${on ? ' askone__theme--on' : ''}`} aria-pressed={on} onClick={() => onPick(on ? null : t.id)}>
                  <span className="askone__themet">{t.title}</span>
                  <span className="askone__themen">{t.asks} {t.asks === 1 ? 'ask' : 'asks'}</span>
                  <span className="askone__split" aria-hidden="true">
                    <b className="askone__bar--direct" style={{ inlineSize: w(t.nDirect) }} />
                    <b className="askone__bar--inferred" style={{ inlineSize: w(t.nInferred) }} />
                    <b className="askone__bar--none" style={{ inlineSize: w(t.nNone) }} />
                  </span>
                  <span className="askone__themesum">{t.summary}</span>
                  {t.gap && <span className="askone__themesum"><strong>Gap.</strong> {t.gap}</span>}
                  <span className="askone__themeact">Suggested: {THEME_ACTIONS[t.suggestedAction]}</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
      {themes && themes.length > 0 && <p className="askone__note">Press a theme to narrow the list to it.</p>}
    </section>
  )
}

/* ── where people went next ──────────────────────────────────────────────── */

export function AskOneNext({ summary }: { summary: AskOneSummary }) {
  const rows = summary.outcome
  const reported = rows.filter((r) => r.outcome !== 'unreported')
  const byOutcome = new Map<string, number>()
  for (const r of rows) byOutcome.set(r.outcome, (byOutcome.get(r.outcome) ?? 0) + r.n)
  const corpusKeys = Object.keys(summary.corpus).sort()
  return (
    <div className="askone__next">
      <h3 className="askone__h">Where people went next</h3>
      {reported.length === 0 ? (
        <p className="askone__empty">
          {summary.recordedSince
            ? 'Nobody has sent a question on from Ask One in this period.'
            : 'Not recorded yet. It starts with the first conversation after 29 Sep.'}
        </p>
      ) : (
        <ul className="askone__kv">
          {(['board', 'platform', 'technical', 'hr', 'unreported'] as const).map((k) =>
            byOutcome.get(k) ? (
              <li key={k}><span>{OUTCOME_WORDS[k]}</span><b>{byOutcome.get(k)}</b></li>
            ) : null,
          )}
        </ul>
      )}
      <p className="askone__note">“Nothing reported” means the person closed the drawer. It never means they were satisfied.</p>

      <h3 className="askone__h">Which guide answered</h3>
      {corpusKeys.length === 0 || (corpusKeys.length === 1 && corpusKeys[0] === 'unrecorded') ? (
        <p className="askone__empty">Not recorded before 29 Sep.</p>
      ) : (
        <ul className="askone__kv">
          {corpusKeys.map((k) => (
            <li key={k}><span>{CORPUS_WORDS[k] ?? k}</span><b>{summary.corpus[k]}</b></li>
          ))}
        </ul>
      )}

      <h3 className="askone__h">Sent in the same period</h3>
      <ul className="askone__kv">
        <li><span>{OUTCOME_WORDS.board}</span><b>{summary.sent.board}</b></li>
        <li><span>{OUTCOME_WORDS.platform}</span><b>{summary.sent.platform}</b></li>
        <li><span>{OUTCOME_WORDS.technical}</span><b>{summary.sent.technical}</b></li>
        <li><span>{OUTCOME_WORDS.hr}</span><b>{summary.sent.hr}</b></li>
      </ul>
      <p className="askone__note">Counted separately and not linked to any conversation, by design.</p>
    </div>
  )
}

/* ── the list ────────────────────────────────────────────────────────────── */

export function BasisPills({ q }: { q: AskOneQuestion }) {
  return (
    <>
      {q.nDirect > 0 && <span className="pill pill--live">Answered</span>}
      {q.nInferred > 0 && <span className="pill pill--draft">Inferred</span>}
      {q.nNone > 0 && <span className="pill pill--kind">No answer</span>}
      {q.nUnavailable > 0 && <span className="pill pill--stale">Outage</span>}
      {q.times === 0 && <span className="pill pill--stale">Rated on a follow-up</span>}
    </>
  )
}

export function stateWords(q: AskOneQuestion): string | null {
  if (!q.reviewState) return null
  if (q.reviewState === 'drafted') return q.draftPublished ? 'Answered in Wiki One' : 'Draft in Wiki One'
  if (q.reviewState === 'sent') return q.trackerIssueId ? `${q.trackerIssueId} · ${q.trackerStatus ?? 'filed'}` : 'Waiting for the 07:00 run'
  return q.reviewState === 'reviewed' ? 'Reviewed' : 'Dismissed'
}

export function AskOneList({
  rows,
  total,
  counts,
  filter,
  onFilter,
  search,
  onSearch,
  themeTitle,
  onClearTheme,
  openKey,
  onOpen,
}: {
  rows: AskOneQuestion[] | null
  total: number
  counts: Record<AskOneFilter, number> | null
  filter: AskOneFilter
  onFilter: (f: AskOneFilter) => void
  search: string
  onSearch: (s: string) => void
  themeTitle: string | null
  onClearTheme: () => void
  openKey: string | null
  onOpen: (key: string) => void
}) {
  return (
    <div className="askone__list">
      <div className="queue__tools">
        <div className="chips" role="group" aria-label="Filter">
          {ASK_ONE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`chip chip--sm${filter === f.key ? ' chip--on' : ''}`}
              aria-pressed={filter === f.key}
              onClick={() => onFilter(f.key)}
            >
              {f.label}
              {counts && <span className="chip__n">{counts[f.key]}</span>}
            </button>
          ))}
          {themeTitle && (
            <button type="button" className="chip chip--sm chip--on" aria-pressed="true" onClick={onClearTheme}>
              Theme: {themeTitle} ×
            </button>
          )}
        </div>
        <div>
          <label className="sr-only" htmlFor="askone-search">Search what people asked</label>
          {/* The SERVER searches (p_search, folded like the key), so this is
              the whole period and not the page — standing rule 1. */}
          <input
            id="askone-search"
            className="field__input"
            type="search"
            placeholder="Search what people asked"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
      </div>
      {rows === null ? (
        <p className="queue__empty">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="queue__empty">{filter === 'action' ? 'Nothing needs a look.' : 'Nothing here for this filter and period.'}</p>
      ) : (
        <ul className="queue__list">
          {rows.map((q) => {
            const state = stateWords(q)
            return (
              <li key={q.foldKey}>
                <button
                  type="button"
                  className={`askone__row${openKey === q.foldKey ? ' askone__row--on' : ''}`}
                  aria-current={openKey === q.foldKey ? 'true' : undefined}
                  onClick={() => onOpen(q.foldKey)}
                >
                  <span className="askone__q">{q.sample}</span>
                  <span className="askone__times" title={q.times === 0 ? 'Rated, never asked as a first turn' : undefined}>×{q.times}</span>
                  <span className="askone__meta">
                    <BasisPills q={q} />
                    {q.downs > 0 && <span className="pill askone__down">▼ {q.downs}</span>}
                    {q.ups > 0 && <span className="pill pill--kind">▲ {q.ups}</span>}
                    {state && <span className="pill pill--star">{state}</span>}
                    {q.askedAgain && <span className="pill pill--draft">Asked again since</span>}
                    <span>{spanLabel(q.firstAsked, q.lastAsked)}</span>
                    {q.themeTitle && <span>· {q.themeTitle}</span>}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
      {rows && rows.length > 0 && (
        <p className="queue__foot">
          {total > rows.length ? `Showing the ${rows.length} most asked of ${total}. ` : ''}
          Grouped by wording, so two spellings of one question are one line. Nobody is named — these logs carry no person.
        </p>
      )}
    </div>
  )
}

/* ── one question, and the four acts ─────────────────────────────────────── */

type Form = 'reviewed' | 'dismissed' | 'draft' | 'dev' | null

export function AskOneDetail({
  q,
  canDraft,
  busy,
  error,
  onReview,
  onReopen,
  onDraft,
  onSend,
}: {
  q: AskOneQuestion
  /** content.draft. The tenant admin reads and acts but does not author Wiki One (B18, 085's split-by-act). */
  canDraft: boolean
  busy: boolean
  error: string | null
  onReview: (state: 'reviewed' | 'dismissed', reason: AskOneReason) => void
  onReopen: () => void
  onDraft: (question: string, answer: string) => void
  onSend: (title: string, body: string) => void
}) {
  const [form, setForm] = useState<Form>(null)
  const [dq, setDq] = useState(q.sample.charAt(0).toUpperCase() + q.sample.slice(1))
  const [da, setDa] = useState('')
  const [dt, setDt] = useState('')
  const [db, setDb] = useState('')
  const state = stateWords(q)
  const open = !q.reviewState || q.askedAgain
  return (
    <section className="askone__detail" aria-label="The question">
      <h3 className="askone__detailq">{q.sample}</h3>
      <dl className="askone__dl">
        <dt>Asked</dt>
        <dd>{q.times === 0 ? 'Only on a follow-up (rated, never a first turn)' : `${q.times} ${q.times === 1 ? 'time' : 'times'}`}</dd>
        <dt>When</dt>
        <dd>{spanLabel(q.firstAsked, q.lastAsked)}</dd>
        <dt>One</dt>
        <dd><BasisPills q={q} /></dd>
        <dt>Guide</dt>
        <dd>{q.corpora.length ? q.corpora.map((c) => CORPUS_WORDS[c] ?? c).join(', ') : 'Not recorded'}</dd>
        <dt>Went next</dt>
        <dd>{q.outcomes.length ? q.outcomes.map((o) => OUTCOME_WORDS[o]).join(', ') : 'Nothing reported'}</dd>
        {q.themeTitle && (
          <>
            <dt>Theme</dt>
            <dd>{q.themeTitle}</dd>
          </>
        )}
      </dl>

      {q.downs > 0 && q.ratedAnswer && (
        /* 048's ONE exception, unchanged: One's text is shown after the fact
           only because somebody pointed at it by rating it wrong, only to the
           people who can fix it, labelled as a rating, behind a disclosure. */
        <details className="askone__rated">
          <summary>
            Rated wrong ×{q.downs}
            {q.downReasons.length ? ` · ${q.downReasons.map((r) => REASON_WORDS[r] ?? r).join(', ')}` : ''}
          </summary>
          <p><strong>The answer that was rated:</strong> {q.ratedAnswer}</p>
        </details>
      )}
      <p className="askone__note">Nobody is named. The log carries the words, how One answered and a date.</p>

      {error && <p className="roles__error" role="alert">{error}</p>}

      {state && (
        <p className="askone__state" aria-live="polite">
          <strong>{state}</strong>
          {q.reviewReason ? ` · ${reasonLabel(q.reviewReason)}` : ''}
          {q.reviewedOn ? ` · ${dayLabel(q.reviewedOn)}` : ''}
          {q.askedAgain ? ' · asked again since' : ''}
        </p>
      )}
      {q.reviewState && (q.reviewState === 'reviewed' || q.reviewState === 'dismissed') && (
        <button type="button" className="btn btn--quiet btn--sm" onClick={onReopen} disabled={busy}>
          Put it back on the list
        </button>
      )}

      {open && (
        <div className="askone__acts">
          <button type="button" className={`btn btn--sm${form === 'reviewed' ? ' btn--primary' : ''}`} onClick={() => setForm(form === 'reviewed' ? null : 'reviewed')} disabled={busy}>
            Mark reviewed
          </button>
          <button type="button" className={`btn btn--sm${form === 'dismissed' ? ' btn--primary' : ''}`} onClick={() => setForm(form === 'dismissed' ? null : 'dismissed')} disabled={busy}>
            Dismiss
          </button>
          {!q.draftContentId && (
            <button type="button" className={`btn btn--sm${form === 'draft' ? ' btn--primary' : ''}`} onClick={() => setForm(form === 'draft' ? null : 'draft')} disabled={busy || !canDraft}>
              Draft a Wiki One answer
            </button>
          )}
          <button type="button" className={`btn btn--sm${form === 'dev' ? ' btn--primary' : ''}`} onClick={() => setForm(form === 'dev' ? null : 'dev')} disabled={busy}>
            Send to developers
          </button>
        </div>
      )}
      {open && !canDraft && !q.draftContentId && (
        <p className="askone__note">Drafting a Wiki One answer needs content.draft, which comms admins hold. This role reads and acts, and does not author.</p>
      )}

      {(form === 'reviewed' || form === 'dismissed') && (
        <div className="supportstaff__form">
          <span className="supportstaff__label">{form === 'reviewed' ? 'Why it is reviewed' : 'Why it is dismissed'}</span>
          <div className="chips" role="group" aria-label="Reason">
            {REVIEW_REASONS[form].map((r) => (
              <button
                key={r.key}
                type="button"
                className="chip chip--sm"
                disabled={busy}
                onClick={() => {
                  onReview(form, r.key)
                  setForm(null)
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
          <p className="askone__note">If these words are asked again after today, the line comes back marked “asked again since”.</p>
        </div>
      )}

      {form === 'draft' && canDraft && (
        <form
          className="supportstaff__form"
          onSubmit={(e) => {
            e.preventDefault()
            if (!dq.trim() || !da.trim()) return
            onDraft(dq.trim(), da.trim())
            setForm(null)
          }}
        >
          <label className="supportstaff__label" htmlFor="askone-dq">Question, as it will appear in Wiki One</label>
          <input id="askone-dq" className="composer__body supportstaff__title" maxLength={300} value={dq} onChange={(e) => setDq(e.target.value)} disabled={busy} />
          <label className="supportstaff__label" htmlFor="askone-da">Answer. You write this; One does not</label>
          <textarea id="askone-da" className="composer__body" rows={6} value={da} onChange={(e) => setDa(e.target.value)} disabled={busy} />
          <p className="askone__note">Saved as a draft authored by you. A curator publishes it, and until then One does not read it.</p>
          <div className="supportstaff__actions">
            <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !dq.trim() || !da.trim()}>Save draft</button>
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => setForm(null)} disabled={busy}>Cancel</button>
          </div>
        </form>
      )}

      {form === 'dev' && (
        <form
          className="supportstaff__form"
          onSubmit={(e) => {
            e.preventDefault()
            if (!dt.trim() || !db.trim()) return
            onSend(dt.trim(), db.trim())
            setForm(null)
          }}
        >
          <p className="supportstaff__warn">
            Developers see only this. Write it so nobody could tell who asked — no name, no business unit or site, none of
            their exact words. Paraphrase.
          </p>
          <label className="supportstaff__label" htmlFor="askone-dt">Title</label>
          <input id="askone-dt" className="composer__body supportstaff__title" maxLength={200} value={dt} onChange={(e) => setDt(e.target.value)} disabled={busy} />
          <label className="supportstaff__label" htmlFor="askone-db">What happens, where, and what was expected</label>
          <textarea id="askone-db" className="composer__body" rows={6} maxLength={8000} value={db} onChange={(e) => setDb(e.target.value)} disabled={busy} />
          <p className="askone__note">The 07:00 run files it as SUP-n through the same boundary tickets use.</p>
          <div className="supportstaff__actions">
            <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !dt.trim() || !db.trim()}>Approve for developers</button>
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => setForm(null)} disabled={busy}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  )
}

/* The five reasons' words live ONCE, in lib/wiki.ts (048). */
const REASON_WORDS: Record<string, string> = Object.fromEntries(ONE_REASONS.map((r) => [r.key, r.label]))
